import { Injectable } from '@angular/core';
import moment from 'moment';
import { CpNotification, NotificationAction, NotificationLevel } from '../contracts/socket/notification-req';

const STORAGE_KEY = 'cp.notifications';
const MAX_ITEMS = 100;
const MAX_AGE_DAYS = 30;

interface PersistedNotification {
    id?: number;
    title: string;
    messages: string[];
    createdAtUtc: string;
    read: boolean;
    success: boolean;
    level?: NotificationLevel;
    action?: NotificationAction;
    source?: string;
    category?: string;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationStoreService {
    load(): CpNotification[] {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return [];
            }

            const parsed = JSON.parse(raw) as unknown;
            if (!Array.isArray(parsed)) {
                return [];
            }

            const minDate = moment.utc().subtract(MAX_AGE_DAYS, 'days');

            return parsed
                .filter((item): item is PersistedNotification => this.isPersistedNotification(item))
                .filter(item => {
                    const createdAt = moment(item.createdAtUtc);
                    return createdAt.isValid() && createdAt.isSameOrAfter(minDate);
                })
                .sort((a, b) => moment(b.createdAtUtc).valueOf() - moment(a.createdAtUtc).valueOf())
                .slice(0, MAX_ITEMS)
                .map(item => new CpNotification(
                    item.title,
                    item.messages,
                    item.success,
                    {
                        id: item.id,
                        read: item.read,
                        createdAtUtc: item.createdAtUtc,
                        level: item.level,
                        action: item.action,
                        source: item.source,
                        category: item.category
                    }
                ));
        } catch (error) {
            console.error('[NotificationStoreService] load failed', error);
            return [];
        }
    }

    save(items: CpNotification[]): void {
        try {
            const minDate = moment.utc().subtract(MAX_AGE_DAYS, 'days');

            const persisted: PersistedNotification[] = items
                .filter(item => !!item)
                .filter(item => item.createdAtUtc?.isValid?.() ?? false)
                .filter(item => item.createdAtUtc.isSameOrAfter(minDate))
                .sort((a, b) => b.createdAtUtc.valueOf() - a.createdAtUtc.valueOf())
                .slice(0, MAX_ITEMS)
                .map(item => ({
                    id: item.id,
                    title: item.title ?? '',
                    messages: Array.isArray(item.messages) ? item.messages.filter(x => !!x) : [],
                    createdAtUtc: item.createdAtUtc.toISOString(),
                    read: !!item.read,
                    success: !!item.success,
                    level: item.level,
                    action: item.action,
                    source: item.source,
                    category: item.category
                }));

            localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
        } catch (error) {
            console.error('[NotificationStoreService] save failed', error);
        }
    }

    clear(): void {
        localStorage.removeItem(STORAGE_KEY);
    }

    private isPersistedNotification(value: unknown): value is PersistedNotification {
        if (!value || typeof value !== 'object') {
            return false;
        }

        const item = value as PersistedNotification;

        return typeof item.title === 'string'
            && Array.isArray(item.messages)
            && typeof item.createdAtUtc === 'string'
            && typeof item.read === 'boolean'
            && typeof item.success === 'boolean';
    }
}